import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import type {
  Asset,
  AssetFolder,
  ProgramDetail,
  ProgramEmailPrefs,
  ProgramRewardDuration,
  ProgramRewardType,
  ProgramRewardUnit,
  ProgramUpdateRequest,
} from "../../shared/types";
import "../styles/program-settings.css";

/**
 * Program settings modal (node B10), opened from the partners list "Settings".
 * Tabs Reward / Rules / Assets / Documents / Emails. Reward + Rules + Emails are
 * REAL owner config, loaded from GET /products/:id/program and saved back through
 * PUT /products/:id/program (S1, plus the emails field from migration 0013). The
 * Assets tab is a read-only reflection of the product's shared library (B4/S4),
 * and Documents shows the real agreement version and requirement state. No mock
 * settings and no toggle that persists nowhere.
 */

type Tab = "reward" | "rules" | "assets" | "documents" | "emails";

const CURRENCIES: ReadonlyArray<{ code: string; symbol: string; label: string }> = [
  { code: "USD", symbol: "$", label: "US Dollar" },
  { code: "EUR", symbol: "€", label: "Euro" },
  { code: "GBP", symbol: "£", label: "British Pound" },
  { code: "CAD", symbol: "$", label: "Canadian Dollar" },
  { code: "AUD", symbol: "$", label: "Australian Dollar" },
];

const RATE_PRESETS = [10, 20, 30] as const;

function currencySymbol(code: string): string {
  return CURRENCIES.find((c) => c.code === code)?.symbol ?? "$";
}

/** A raw stored value (bps or cents) shown as a plain number (both divide by 100). */
function rawToNum(raw: number): number {
  return raw / 100;
}
/** A typed number back to the stored raw value (percent points to bps, dollars to cents). */
function numToRaw(n: number): number {
  return Math.round(n * 100);
}

/** A whole, non-negative integer from a text field, with a fallback. */
function parseCount(s: string, fallback: number): number {
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}
/** A non-negative number from a text field, with a fallback. */
function parseAmount(s: string, fallback: number): number {
  const n = Number.parseFloat(s);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/**
 * The referral link base stores the full prefix a partner code is appended to,
 * e.g. "https://flashkut.ai/go?p=" so that base + slug is the whole link. The
 * Rules field shows a clean host and path, so strip the scheme and the trailing
 * "?p=" for display, and rebuild a valid base on save.
 */
function linkBaseToDisplay(linkBase: string | null): string {
  if (!linkBase) return "";
  return linkBase.replace(/^https?:\/\//, "").replace(/\?p=$/, "");
}
function displayToLinkBase(display: string): string | null {
  const clean = display
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\?p=$/, "")
    .replace(/\/+$/, "");
  if (!clean) return null;
  return `https://${clean}?p=`;
}

/** One editable ladder rung in the Reward tab. */
interface TierDraft {
  key: string;
  /** Sales ceiling for this rung, or null for the top, unbounded band ("above"). */
  upToCount: number | null;
  /** Display value (percent points or currency units), read with the reward unit. */
  value: string;
}

function newKey(): string {
  return Math.random().toString(36).slice(2, 9);
}

/** Prototype default ladder used when a program first switches to "Rises with volume". */
function defaultTiers(): TierDraft[] {
  return [
    { key: newKey(), upToCount: 10, value: "20" },
    { key: newKey(), upToCount: 25, value: "25" },
    { key: newKey(), upToCount: null, value: "30" },
  ];
}

// ── toggle switch (matches the prototype .sw2, violet when on) ────────────────
function Switch({
  on,
  onToggle,
  label,
}: {
  on: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={`ps-sw${on ? " on" : ""}`}
      onClick={onToggle}
    />
  );
}

/**
 * The program's settings as a draft: loaded from GET /products/:id/program, edited field by field and
 * saved whole through PUT. The computer's modal and the phone's Program screens both edit through it.
 */
export function useProgramSettings(productId: string) {
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  // ── Reward ──
  const [rewardType, setRewardType] = useState<ProgramRewardType>("flat");
  const [rewardUnit, setRewardUnit] = useState<ProgramRewardUnit>("percent");
  const [rewardValue, setRewardValue] = useState("20");
  const [tiers, setTiers] = useState<TierDraft[]>([]);
  const [duration, setDuration] = useState<ProgramRewardDuration>("recurring");
  const [bonus, setBonus] = useState("0");

  // ── Rules ──
  const [linkBaseInput, setLinkBaseInput] = useState("");
  const [cookieDays, setCookieDays] = useState("30");
  const [holdDays, setHoldDays] = useState("30");
  const [minPayout, setMinPayout] = useState("50");
  const [currency, setCurrency] = useState("USD");
  const [couponEnabled, setCouponEnabled] = useState(false);

  // ── Emails ──
  const [emailPrefs, setEmailPrefs] = useState<ProgramEmailPrefs>({
    newSale: true,
    rewardReady: true,
    payoutSent: true,
    inactivityNudge: false,
  });

  // ── Assets (read-only reflection of the product library) ──
  const [folders, setFolders] = useState<AssetFolder[] | null>(null);
  const [rootShared, setRootShared] = useState(0);

  // ── Documents (real agreement version) ──
  const [agreementVersion, setAgreementVersion] = useState<number | null>(null);

  // What was last loaded or saved, so a phone shows Save only once something changed.
  const loaded = useRef<ProgramDetail | null>(null);
  const [hydrated, setHydrated] = useState(0);
  const [baseline, setBaseline] = useState<string | null>(null);

  const hydrate = useCallback((detail: ProgramDetail) => {
    loaded.current = detail;
    const p = detail.program;
    setRewardType(p.rewardType);
    setRewardUnit(p.rewardUnit);
    setRewardValue(String(rawToNum(p.rewardValueBpsOrCents)));
    setTiers(
      detail.tiers.length > 0
        ? detail.tiers.map((t) => ({
            key: newKey(),
            upToCount: t.upToCount,
            value: String(rawToNum(t.rewardCentsOrBps)),
          }))
        : defaultTiers(),
    );
    setDuration(p.rewardDuration);
    setBonus(String(rawToNum(p.bonusFirstSaleCents)));
    setLinkBaseInput(linkBaseToDisplay(p.linkBase));
    setCookieDays(String(p.cookieDays));
    setHoldDays(String(p.holdDays));
    setMinPayout(String(rawToNum(p.minPayoutCents)));
    setCurrency(p.currency || "USD");
    setCouponEnabled(p.couponEnabled);
    setEmailPrefs(p.emailPrefs);
    setHydrated((n) => n + 1);
  }, []);

  // Load the real program and hydrate every editable field from it.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadErr(null);
    api.partners
      .program(productId)
      .then((detail) => {
        if (!alive) return;
        hydrate(detail);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setLoadErr(e instanceof ApiError ? e.message : String(e));
        setLoading(false);
      });
    // Best-effort side loads: the Assets tab (shared library) and the real
    // agreement version for Documents. Neither blocks editing Reward / Rules.
    api.assets
      .list(productId)
      .then((res) => {
        if (!alive) return;
        setFolders(res.folders);
        setRootShared(res.assets.filter((a: Asset) => a.folderId === null && a.sharedWithPartners).length);
      })
      .catch(() => {});
    api.agreements
      .get(productId)
      .then((res) => alive && setAgreementVersion(res.template.version))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [productId, hydrate]);

  const sym = currencySymbol(currency);
  const rewardValueNum = Number.parseFloat(rewardValue);
  const rewardValid = Number.isFinite(rewardValueNum) && rewardValueNum > 0;
  const activePreset = RATE_PRESETS.find((p) => p === Number(rewardValue)) ?? null;
  const rateSuffix = rewardUnit === "percent" ? "%" : sym;

  // ── tier editing ──
  const setTierCount = (key: string, raw: string) => {
    setTiers((prev) =>
      prev.map((t) => (t.key === key ? { ...t, upToCount: parseCount(raw, t.upToCount ?? 0) } : t)),
    );
  };
  const setTierValue = (key: string, value: string) => {
    setTiers((prev) => prev.map((t) => (t.key === key ? { ...t, value } : t)));
  };
  const removeTier = (key: string) => {
    setTiers((prev) => prev.filter((t) => t.key !== key));
  };
  const addTier = () => {
    setTiers((prev) => {
      const topIdx = prev.findIndex((t) => t.upToCount === null);
      const rung: TierDraft = { key: newKey(), upToCount: 50, value: rewardValue || "20" };
      if (topIdx === -1) return [...prev, rung];
      const copy = [...prev];
      copy.splice(topIdx, 0, rung);
      return copy;
    });
  };

  const switchRewardType = (next: ProgramRewardType) => {
    if (next === "ladder" && tiers.length === 0) setTiers(defaultTiers());
    setRewardType(next);
  };

  const setEmail = (key: keyof ProgramEmailPrefs) => {
    setEmailPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const buildPayload = useCallback((): ProgramUpdateRequest => {
    const payload: ProgramUpdateRequest = {
      rewardType,
      rewardUnit,
      rewardValueBpsOrCents: numToRaw(rewardValid ? rewardValueNum : 0),
      rewardDuration: duration,
      holdDays: parseCount(holdDays, 30),
      cookieDays: parseCount(cookieDays, 30),
      minPayoutCents: numToRaw(parseAmount(minPayout, 0)),
      currency,
      bonusFirstSaleCents: numToRaw(parseAmount(bonus, 0)),
      couponEnabled,
      linkBase: displayToLinkBase(linkBaseInput),
      emailPrefs,
    };
    if (rewardType === "ladder") {
      payload.tiers = tiers.map((t) => ({
        upToCount: t.upToCount,
        rewardCentsOrBps: numToRaw(parseAmount(t.value, 0)),
      }));
    }
    return payload;
  }, [
    rewardType,
    rewardUnit,
    rewardValid,
    rewardValueNum,
    duration,
    holdDays,
    cookieDays,
    minPayout,
    currency,
    bonus,
    couponEnabled,
    linkBaseInput,
    emailPrefs,
    tiers,
  ]);

  // Why: the hydrated fields land together on the next render, so the baseline is read from that render.
  const payloadNow = useRef(buildPayload);
  payloadNow.current = buildPayload;
  useEffect(() => {
    if (hydrated) setBaseline(JSON.stringify(payloadNow.current()));
  }, [hydrated]);
  const dirty = baseline !== null && JSON.stringify(buildPayload()) !== baseline;

  /** Puts every field back to what was last loaded or saved. */
  const reset = useCallback(() => {
    if (loaded.current) hydrate(loaded.current);
    setSaveErr(null);
  }, [hydrate]);

  /** Saves the whole draft, which becomes the new baseline. Null when the save failed. */
  const save = useCallback(async (): Promise<ProgramDetail | null> => {
    if (saving || loading) return null;
    setSaving(true);
    setSaveErr(null);
    try {
      const detail = await api.partners.updateProgram(productId, buildPayload());
      hydrate(detail);
      setSaving(false);
      return detail;
    } catch (e) {
      setSaveErr(e instanceof ApiError ? e.message : String(e));
      setSaving(false);
      return null;
    }
  }, [saving, loading, productId, buildPayload, hydrate]);

  /** Shares a whole folder with partners, or stops sharing it, straight away. False when that failed. */
  const shareFolder = useCallback(async (folder: AssetFolder, shared: boolean): Promise<boolean> => {
    const flip = (to: boolean) =>
      setFolders((prev) => prev?.map((f) => (f.id === folder.id ? { ...f, sharedWithPartners: to } : f)) ?? prev);
    flip(shared);
    try {
      await api.assets.patchFolder(folder.id, { sharedWithPartners: shared });
      return true;
    } catch {
      flip(!shared);
      return false;
    }
  }, []);

  return {
    loading,
    loadErr,
    saving,
    saveErr,
    rewardType,
    rewardUnit,
    setRewardUnit,
    rewardValue,
    setRewardValue,
    tiers,
    duration,
    setDuration,
    bonus,
    setBonus,
    linkBaseInput,
    setLinkBaseInput,
    cookieDays,
    setCookieDays,
    holdDays,
    setHoldDays,
    minPayout,
    setMinPayout,
    currency,
    setCurrency,
    couponEnabled,
    setCouponEnabled,
    emailPrefs,
    folders,
    rootShared,
    agreementVersion,
    sym,
    rewardValid,
    activePreset,
    rateSuffix,
    setTierCount,
    setTierValue,
    removeTier,
    addTier,
    switchRewardType,
    setEmail,
    dirty,
    reset,
    save,
    shareFolder,
  };
}

export function ProgramSettingsModal({
  productId,
  productName,
  onClose,
  onSaved,
  onEditAgreement,
  onOpenLibrary,
}: {
  productId: string;
  productName: string;
  onClose: () => void;
  onSaved: (detail: ProgramDetail) => void;
  /** Close the modal and route toward the agreement builder (node B11). */
  onEditAgreement: () => void;
  /** Close the modal and open the product's asset library (node B4). */
  onOpenLibrary: () => void;
}) {
  const [tab, setTab] = useState<Tab>("reward");
  const ps = useProgramSettings(productId);
  const {
    loading,
    loadErr,
    saving,
    saveErr,
    rewardType,
    rewardUnit,
    setRewardUnit,
    rewardValue,
    setRewardValue,
    tiers,
    duration,
    setDuration,
    bonus,
    setBonus,
    linkBaseInput,
    setLinkBaseInput,
    cookieDays,
    setCookieDays,
    holdDays,
    setHoldDays,
    minPayout,
    setMinPayout,
    currency,
    setCurrency,
    couponEnabled,
    setCouponEnabled,
    emailPrefs,
    folders,
    rootShared,
    agreementVersion,
    sym,
    activePreset,
    rateSuffix,
    setTierCount,
    setTierValue,
    removeTier,
    addTier,
    switchRewardType,
    setEmail,
  } = ps;

  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = useCallback(async () => {
    const detail = await ps.save();
    if (!detail) return;
    onSaved(detail);
    onClose();
  }, [ps, onSaved, onClose]);

  const sharedFolders = useMemo(
    () => (folders ? folders.filter((f) => f.sharedWithPartners) : []),
    [folders],
  );

  return (
    <div
      className="ps-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ps-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Program settings for ${productName}`}
      >
        <div className="ps-head">
          <h3>Program settings</h3>
          <button type="button" className="ps-x" onClick={onClose} ref={closeRef} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="ps-tabs" role="tablist" aria-label="Program settings tabs">
          {(
            [
              ["reward", "Reward"],
              ["rules", "Rules"],
              ["assets", "Assets"],
              ["documents", "Documents"],
              ["emails", "Emails"],
            ] as ReadonlyArray<[Tab, string]>
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={tab === key ? "on" : ""}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="ps-body">
          {loading && <div className="ps-load">Loading the program</div>}
          {!loading && loadErr && <div className="ps-load err">Couldn't load the program: {loadErr}</div>}

          {!loading && !loadErr && (
            <>
              {tab === "reward" && (
                <div className="ps-pane">
                  <div className="ps-field">
                    <label>How the rate works</label>
                    <div className="ps-seg" role="group" aria-label="How the rate works">
                      <button
                        type="button"
                        className={rewardType === "flat" ? "on" : ""}
                        onClick={() => switchRewardType("flat")}
                      >
                        Flat rate
                      </button>
                      <button
                        type="button"
                        className={rewardType === "ladder" ? "on" : ""}
                        onClick={() => switchRewardType("ladder")}
                      >
                        Rises with volume
                      </button>
                    </div>
                  </div>

                  {rewardType === "flat" ? (
                    <div className="ps-field">
                      <label>
                        Rate <span className="ps-hint">every partner earns this</span>
                      </label>
                      <div className="ps-rate-row">
                        <div className="ps-seg sm" role="group" aria-label="Reward unit">
                          <button
                            type="button"
                            className={rewardUnit === "percent" ? "on" : ""}
                            onClick={() => setRewardUnit("percent")}
                          >
                            Percent
                          </button>
                          <button
                            type="button"
                            className={rewardUnit === "fixed" ? "on" : ""}
                            onClick={() => setRewardUnit("fixed")}
                          >
                            Flat {sym}
                          </button>
                        </div>
                        <div className="ps-presets">
                          {RATE_PRESETS.map((p) => (
                            <button
                              key={p}
                              type="button"
                              className={`ps-preset${activePreset === p ? " on" : ""}`}
                              onClick={() => setRewardValue(String(p))}
                            >
                              {rewardUnit === "percent" ? `${p}%` : `${sym}${p}`}
                            </button>
                          ))}
                          <label className={`ps-preset custom${activePreset === null ? " on" : ""}`}>
                            {rewardUnit === "percent" ? "" : sym}
                            <input
                              inputMode="decimal"
                              value={rewardValue}
                              onChange={(e) => setRewardValue(e.target.value)}
                              aria-label="Custom rate value"
                            />
                            {rewardUnit === "percent" ? "%" : ""}
                          </label>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="ps-field">
                      <label>
                        Volume tiers{" "}
                        <span className="ps-hint">the more a partner sells, the higher their rate</span>
                      </label>
                      <div className="ps-tiers">
                        {tiers.map((t, i) => {
                          const isTop = t.upToCount === null;
                          const prev = i > 0 ? tiers[i - 1].upToCount : null;
                          return (
                            <div className="ps-tier" key={t.key}>
                              <span className="ps-ti">{i + 1}</span>
                              <span className="ps-tx">{isTop ? "above" : "up to"}</span>
                              {isTop ? (
                                <span className="ps-topcount">{prev ?? "the last tier"}</span>
                              ) : (
                                <input
                                  className="ps-tin count"
                                  inputMode="numeric"
                                  value={String(t.upToCount ?? "")}
                                  onChange={(e) => setTierCount(t.key, e.target.value)}
                                  aria-label={`Tier ${i + 1} sales ceiling`}
                                />
                              )}
                              <span className="ps-tx">sales pays</span>
                              <input
                                className="ps-tin rate"
                                inputMode="decimal"
                                value={t.value}
                                onChange={(e) => setTierValue(t.key, e.target.value)}
                                aria-label={`Tier ${i + 1} rate`}
                              />
                              <span className="ps-tx">{rateSuffix}</span>
                              <button
                                type="button"
                                className="ps-tdel"
                                onClick={() => removeTier(t.key)}
                                aria-label={`Remove tier ${i + 1}`}
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <button type="button" className="ps-btn ghost sm" onClick={addTier}>
                        + Add tier
                      </button>
                    </div>
                  )}

                  <div className="ps-field">
                    <label>Pay for how long</label>
                    <div className="ps-seg" role="group" aria-label="Reward duration">
                      <button
                        type="button"
                        className={duration === "first" ? "on" : ""}
                        onClick={() => setDuration("first")}
                      >
                        First payment
                      </button>
                      <button
                        type="button"
                        className={duration === "recurring" ? "on" : ""}
                        onClick={() => setDuration("recurring")}
                      >
                        Recurring
                      </button>
                      <button
                        type="button"
                        className={duration === "forever" ? "on" : ""}
                        onClick={() => setDuration("forever")}
                      >
                        Forever
                      </button>
                    </div>
                  </div>

                  <div className="ps-field last">
                    <label>
                      One-time bonus <span className="ps-hint">paid on a partner's first sale</span>
                    </label>
                    <div className="ps-inline">
                      <div className="ps-unit">
                        <span className="ps-pre">{sym}</span>
                        <input
                          type="number"
                          min="0"
                          value={bonus}
                          onChange={(e) => setBonus(e.target.value)}
                          aria-label="First-sale bonus"
                        />
                      </div>
                      <span className="ps-note">extra, once</span>
                    </div>
                  </div>
                </div>
              )}

              {tab === "rules" && (
                <div className="ps-pane">
                  <div className="ps-field">
                    <label>Partner link sends to</label>
                    <input
                      className="ps-inp"
                      value={linkBaseInput}
                      onChange={(e) => setLinkBaseInput(e.target.value)}
                      placeholder="yoursite.com/go"
                      aria-label="Partner link base"
                    />
                    <span className="ps-note">Each partner's code is added to the end of this link.</span>
                  </div>
                  <div className="ps-frow2">
                    <div className="ps-field">
                      <label>
                        Cookie window <span className="ps-hint">how long a click counts</span>
                      </label>
                      <div className="ps-unit">
                        <input
                          type="number"
                          min="1"
                          value={cookieDays}
                          onChange={(e) => setCookieDays(e.target.value)}
                          aria-label="Cookie window in days"
                        />
                        <span className="ps-suf">days</span>
                      </div>
                    </div>
                    <div className="ps-field">
                      <label>
                        Hold <span className="ps-hint">before a reward pays</span>
                      </label>
                      <div className="ps-unit">
                        <input
                          type="number"
                          min="0"
                          value={holdDays}
                          onChange={(e) => setHoldDays(e.target.value)}
                          aria-label="Hold in days"
                        />
                        <span className="ps-suf">days</span>
                      </div>
                    </div>
                  </div>
                  <div className="ps-frow2">
                    <div className="ps-field">
                      <label>Minimum payout</label>
                      <div className="ps-unit">
                        <span className="ps-pre">{sym}</span>
                        <input
                          type="number"
                          min="0"
                          value={minPayout}
                          onChange={(e) => setMinPayout(e.target.value)}
                          aria-label="Minimum payout"
                        />
                      </div>
                    </div>
                    <div className="ps-field">
                      <label>Currency</label>
                      <select
                        className="ps-sel"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        aria-label="Currency"
                      >
                        {CURRENCIES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.code} · {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="ps-field last">
                    <label>
                      Discount coupon{" "}
                      <span className="ps-hint">the referred customer gets a discount too</span>
                    </label>
                    <div className="ps-togrow">
                      <div className="ps-tt">
                        <div className="ps-tn">Give each partner a coupon code</div>
                        <div className="ps-td">A code each partner shares, like SARAH15. Set the matching discount up in your own checkout.</div>
                      </div>
                      <Switch
                        on={couponEnabled}
                        onToggle={() => setCouponEnabled((v) => !v)}
                        label="Give each partner a coupon code"
                      />
                    </div>
                  </div>
                </div>
              )}

              {tab === "assets" && (
                <div className="ps-pane">
                  <div className="ps-assets-head">
                    <span className="ps-assets-title">Shared with partners</span>
                    <button type="button" className="ps-btn ghost sm" onClick={onOpenLibrary}>
                      Open library
                    </button>
                  </div>
                  {folders === null ? (
                    <div className="ps-load">Loading the library</div>
                  ) : sharedFolders.length === 0 && rootShared === 0 ? (
                    <div className="ps-empty">
                      Nothing is shared with partners yet. Open the product library to share folders and
                      files.
                    </div>
                  ) : (
                    <>
                      <div className="ps-folders">
                        {sharedFolders.map((f) => (
                          <div className="ps-folder" key={f.id}>
                            <span className="ps-fic" aria-hidden="true">
                              &#9707;
                            </span>
                            <div className="ps-fmeta">
                              <div className="ps-fn">{f.name}</div>
                              <div className="ps-fc">
                                {f.itemCount} {f.itemCount === 1 ? "item" : "items"}
                              </div>
                            </div>
                            <span className="ps-share-pill">Shared</span>
                          </div>
                        ))}
                        {rootShared > 0 && (
                          <div className="ps-folder">
                            <span className="ps-fic" aria-hidden="true">
                              &#9707;
                            </span>
                            <div className="ps-fmeta">
                              <div className="ps-fn">Loose files</div>
                              <div className="ps-fc">
                                {rootShared} shared {rootShared === 1 ? "file" : "files"}
                              </div>
                            </div>
                            <span className="ps-share-pill">Shared</span>
                          </div>
                        )}
                      </div>
                      <p className="ps-note2">
                        Sharing is managed in the product library. Open it to share more folders or single
                        files.
                      </p>
                    </>
                  )}
                </div>
              )}

              {tab === "documents" && (
                <div className="ps-pane">
                  <p className="ps-note2 top">
                    What partners provide. A signed agreement and a tax form must be complete before a
                    payout.
                  </p>
                  {/* U0: the mandatory-document set is fixed in the S1 payout gate
                      (agreement signed + tax on file + payout method saved). Making
                      requirements owner-editable, plus adding documents and a tax
                      template, is a later node (GRAPH-v2 section 9, "document
                      requirements" / "mandatory-document set"). Shown read-only here. */}
                  <div className="ps-doc">
                    <div className="ps-doc-info">
                      <div className="ps-doc-name">
                        Partner agreement
                        {agreementVersion !== null && <span className="ps-ver">v{agreementVersion}</span>}
                      </div>
                      <div className="ps-doc-sub">e-signed in the portal</div>
                    </div>
                    <span className="ps-req">Mandatory</span>
                    <button type="button" className="ps-btn ghost sm" onClick={onEditAgreement}>
                      Edit
                    </button>
                  </div>
                  <div className="ps-doc">
                    <div className="ps-doc-info">
                      <div className="ps-doc-name">Tax form (W-9 / W-8)</div>
                      <div className="ps-doc-sub">collected in the portal</div>
                    </div>
                    <span className="ps-req">Mandatory</span>
                  </div>
                  <p className="ps-note2">
                    Editing the agreement makes a version partners re-sign. Changing which documents are
                    required arrives with the agreement builder.
                  </p>
                </div>
              )}

              {tab === "emails" && (
                <div className="ps-pane">
                  {/* U0: these are REAL owner preferences, persisted on the program
                      (migration 0013). No email sender exists yet (GRAPH-v2 section
                      9, "email sender"); turning a toggle on records intent, it does
                      not send mail until the sender ships. */}
                  <div className="ps-togrow first">
                    <div className="ps-tt">
                      <div className="ps-tn">New sale</div>
                      <div className="ps-td">When a referral becomes a customer</div>
                    </div>
                    <Switch on={emailPrefs.newSale} onToggle={() => setEmail("newSale")} label="New sale email" />
                  </div>
                  <div className="ps-togrow">
                    <div className="ps-tt">
                      <div className="ps-tn">Reward ready</div>
                      <div className="ps-td">When a commission clears the hold</div>
                    </div>
                    <Switch
                      on={emailPrefs.rewardReady}
                      onToggle={() => setEmail("rewardReady")}
                      label="Reward ready email"
                    />
                  </div>
                  <div className="ps-togrow">
                    <div className="ps-tt">
                      <div className="ps-tn">Payout sent</div>
                      <div className="ps-td">When you record them as paid</div>
                    </div>
                    <Switch
                      on={emailPrefs.payoutSent}
                      onToggle={() => setEmail("payoutSent")}
                      label="Payout sent email"
                    />
                  </div>
                  <div className="ps-togrow">
                    <div className="ps-tt">
                      <div className="ps-tn">Inactivity nudge</div>
                      <div className="ps-td">If no clicks for 30 days</div>
                    </div>
                    <Switch
                      on={emailPrefs.inactivityNudge}
                      onToggle={() => setEmail("inactivityNudge")}
                      label="Inactivity nudge email"
                    />
                  </div>
                  <p className="ps-note2">
                    Set what partners get told. Sending turns on with the email service in a later update.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="ps-foot">
          {saveErr && <span className="ps-save-err">{saveErr}</span>}
          <button type="button" className="ps-btn ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="ps-btn" onClick={save} disabled={saving || loading || !!loadErr}>
            {saving ? "Saving" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
